import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, LogOut, Mail, Phone, Calendar, Users, MessageSquare, CheckCircle2, Trash2, RefreshCw, Inbox, DollarSign, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'obgh_admin_token';

const huntTypeLabels = {
  deer: 'Cerf',
  bear: 'Ours',
  turkey: 'Dindon',
  multiple: 'Combinaison',
};

const huntTypeColors = {
  deer: 'bg-amber-600',
  bear: 'bg-stone-700',
  turkey: 'bg-red-700',
  multiple: 'bg-orange-600',
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-CA', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const LoginForm = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      toast.success('Connecté');
      onSuccess(data.token);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur de connexion';
      toast.error(typeof msg === 'string' ? msg : 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-stone-900 border border-orange-500/30 p-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-500/10 mb-4">
            <Lock className="w-8 h-8 text-orange-500" />
          </div>
          <h1 className="text-2xl font-bold text-white">Espace Admin</h1>
          <p className="text-stone-400 text-sm mt-2">Outaouais Big Game Hunting</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="password" className="text-white mb-2 block">Mot de passe</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              data-testid="admin-password-input"
              className="bg-stone-800 border-orange-500/30 text-white focus:border-orange-500"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 text-lg disabled:opacity-60"
          >
            {loading ? 'Connexion…' : 'Se Connecter'}
          </Button>
        </form>
      </Card>
      <Toaster position="top-right" />
    </div>
  );
};

const RequestCard = ({ req, onMarkRead, onDelete }) => {
  const hunt = huntTypeLabels[req.huntType] || req.huntType || 'Non spécifié';
  const huntColor = huntTypeColors[req.huntType] || 'bg-stone-700';
  return (
    <Card className={`bg-stone-900 border ${req.read ? 'border-stone-700' : 'border-orange-500'} p-6 transition-all`}
      data-testid={`request-card-${req.id}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-xl font-bold text-white">{req.firstName} {req.lastName}</h3>
            {!req.read && (
              <Badge className="bg-orange-500 text-white">Nouveau</Badge>
            )}
            <Badge className={`${huntColor} text-white`}>{hunt}</Badge>
          </div>
          <p className="text-stone-400 text-sm mt-1">Reçu le {formatDate(req.timestamp)}</p>
        </div>
        <div className="flex gap-2">
          {!req.read && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onMarkRead(req.id)}
              data-testid={`mark-read-${req.id}`}
              className="text-stone-400 hover:text-green-500 hover:bg-stone-800"
              title="Marquer comme lu"
            >
              <CheckCircle2 className="w-4 h-4" />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(req.id)}
            data-testid={`delete-${req.id}`}
            className="text-stone-400 hover:text-red-500 hover:bg-stone-800"
            title="Supprimer"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 text-sm">
        <div className="flex items-center gap-2 text-stone-300">
          <Mail className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <a href={`mailto:${req.email}`} className="text-orange-400 hover:text-orange-300 truncate">{req.email}</a>
        </div>
        <div className="flex items-center gap-2 text-stone-300">
          <Phone className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <a href={`tel:${req.phone}`} className="text-orange-400 hover:text-orange-300">{req.phone}</a>
        </div>
        <div className="flex items-center gap-2 text-stone-300">
          <Calendar className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <span>{req.dates}</span>
        </div>
        <div className="flex items-center gap-2 text-stone-300">
          <Users className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <span>{req.guests} chasseur(s)</span>
        </div>
      </div>

      {req.message && (
        <div className="mt-4 p-3 bg-stone-800 rounded border-l-2 border-orange-500">
          <div className="flex items-start gap-2">
            <MessageSquare className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
            <p className="text-stone-300 text-sm whitespace-pre-wrap">{req.message}</p>
          </div>
        </div>
      )}

      {req.email_sent === false && (
        <div className="mt-3 text-xs text-amber-500">
          ⚠ Email non envoyé{req.email_error ? ` (${req.email_error.substring(0, 80)})` : ''}
        </div>
      )}
    </Card>
  );
};

const priceLabels = {
  bear_camping: 'Ours — Séjour Camping',
  bear_chalet: 'Ours — Séjour Chalet',
  bear_vip: 'Ours — Séjour VIP Saison Complète',
  deer_camping: 'Chevreuil — Séjour Camping',
  deer_chalet: 'Chevreuil — Séjour Chalet',
  deer_vip: 'Chevreuil — Séjour VIP Saison Complète',
  turkey: 'Dindon — Séjour Guidé',
  premium: 'Forfait 5 Étoiles Combiné',
};

const PricesPanel = ({ token, onLogout }) => {
  const [prices, setPrices] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/prices`)
      .then(res => setPrices(res.data))
      .catch(() => toast.error('Erreur de chargement des prix'));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/prices`, { prices }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrices(data);
      toast.success('Prix enregistrés ! Ils sont maintenant visibles sur le site.');
    } catch (err) {
      if (err.response?.status === 401) { toast.error('Session expirée'); onLogout(); }
      else toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (!prices) return <div className="text-center py-20 text-stone-400">Chargement…</div>;

  return (
    <div data-testid="prices-panel">
      <Card className="bg-stone-900 border border-stone-700 p-6">
        <h2 className="text-lg font-bold text-white mb-1">Prix des forfaits</h2>
        <p className="text-stone-400 text-sm mb-6">Modifiez un prix puis cliquez sur Enregistrer. Le site est mis à jour immédiatement (français et anglais).</p>
        <div className="grid sm:grid-cols-2 gap-5">
          {Object.keys(priceLabels).map(key => (
            <div key={key}>
              <Label htmlFor={`price-${key}`} className="text-white mb-2 block text-sm">{priceLabels[key]}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-500 font-bold">$</span>
                <Input
                  id={`price-${key}`}
                  data-testid={`price-input-${key}`}
                  value={prices[key] || ''}
                  onChange={(e) => setPrices(p => ({ ...p, [key]: e.target.value }))}
                  className="bg-stone-800 border-orange-500/30 text-white focus:border-orange-500 pl-8"
                />
              </div>
            </div>
          ))}
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-prices-button"
          className="mt-8 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-5 disabled:opacity-60"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Enregistrement…' : 'Enregistrer les Prix'}
        </Button>
      </Card>
    </div>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread
  const [tab, setTab] = useState('requests'); // requests | prices

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/contact-requests`, { headers: authHeaders }),
        axios.get(`${API}/admin/stats`, { headers: authHeaders }),
      ]);
      setRequests(reqRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expirée');
        onLogout();
      } else {
        toast.error('Erreur de chargement');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleMarkRead = async (id) => {
    try {
      await axios.post(`${API}/admin/contact-requests/${id}/read`, {}, { headers: authHeaders });
      setRequests(prev => prev.map(r => r.id === id ? { ...r, read: true } : r));
      setStats(s => ({ ...s, unread: Math.max(0, s.unread - 1) }));
      toast.success('Marqué comme lu');
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande définitivement ?')) return;
    try {
      await axios.delete(`${API}/admin/contact-requests/${id}`, { headers: authHeaders });
      setRequests(prev => prev.filter(r => r.id !== id));
      setStats(s => ({ total: Math.max(0, s.total - 1), unread: s.unread }));
      toast.success('Supprimé');
    } catch {
      toast.error('Erreur');
    }
  };

  const filtered = filter === 'unread' ? requests.filter(r => !r.read) : requests;

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="bg-stone-900 border-b border-orange-500/30 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">
              <span className="text-orange-500">OBGH</span> · Admin
            </h1>
            <p className="text-xs text-stone-400">Demandes de réservation</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              data-testid="refresh-button"
              className="text-stone-300 hover:text-white hover:bg-stone-800"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              data-testid="logout-button"
              className="text-stone-300 hover:text-red-500 hover:bg-stone-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-8">
          <Button
            size="sm"
            onClick={() => setTab('requests')}
            data-testid="tab-requests"
            className={tab === 'requests' ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-stone-800 text-stone-300 hover:bg-stone-700'}
          >
            <Inbox className="w-4 h-4 mr-2" />
            Demandes
          </Button>
          <Button
            size="sm"
            onClick={() => setTab('prices')}
            data-testid="tab-prices"
            className={tab === 'prices' ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-stone-800 text-stone-300 hover:bg-stone-700'}
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Prix
          </Button>
        </div>

        {tab === 'prices' ? (
          <PricesPanel token={token} onLogout={onLogout} />
        ) : (
        <>
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <Card className="bg-stone-900 border border-stone-700 p-6">
            <p className="text-stone-400 text-sm">Total des demandes</p>
            <p className="text-3xl font-bold text-white mt-1" data-testid="stat-total">{stats.total}</p>
          </Card>
          <Card className="bg-stone-900 border border-orange-500 p-6">
            <p className="text-orange-500 text-sm font-semibold">Non lues</p>
            <p className="text-3xl font-bold text-white mt-1" data-testid="stat-unread">{stats.unread}</p>
          </Card>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <Button
            size="sm"
            variant={filter === 'all' ? 'default' : 'ghost'}
            onClick={() => setFilter('all')}
            data-testid="filter-all"
            className={filter === 'all' ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'text-stone-300 hover:bg-stone-800'}
          >
            Toutes ({requests.length})
          </Button>
          <Button
            size="sm"
            variant={filter === 'unread' ? 'default' : 'ghost'}
            onClick={() => setFilter('unread')}
            data-testid="filter-unread"
            className={filter === 'unread' ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'text-stone-300 hover:bg-stone-800'}
          >
            Non lues ({stats.unread})
          </Button>
        </div>

        {loading && requests.length === 0 ? (
          <div className="text-center py-20 text-stone-400">Chargement…</div>
        ) : filtered.length === 0 ? (
          <Card className="bg-stone-900 border border-stone-700 p-12 text-center">
            <Inbox className="w-12 h-12 text-stone-600 mx-auto mb-4" />
            <p className="text-stone-400">
              {filter === 'unread' ? 'Aucune demande non lue 🎉' : 'Aucune demande pour le moment'}
            </p>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="requests-list">
            {filtered.map(req => (
              <RequestCard
                key={req.id}
                req={req}
                onMarkRead={handleMarkRead}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
        </>
        )}
      </main>

      <Toaster position="top-right" />
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    toast.success('Déconnecté');
  };

  if (!token) {
    return <LoginForm onSuccess={setToken} />;
  }
  return <Dashboard token={token} onLogout={handleLogout} />;
}
